import { axiosRetryPost } from "../../utilities/AxiosRetryUtil";
import { logger } from "../../utilities/logger";
import { parseFilterValueMarkup } from "../Panels/CMFileFilterPanel/filterValueParsing";

/**
 * Server-side distinct filter values for the cross-library "All documents" view.
 *
 * The panel's checkbox lists are otherwise harvested from the rows on screen — which is
 * the frontier-limited, capped, emitted window, not the library. On a site with one busy
 * library that can mean only the values touched in the last few hours are offered.
 *
 * `RenderListFilterData` is the endpoint SharePoint's own filter pane uses: it returns a
 * field's distinct values, server-computed, in ONE request. The single-library panel has
 * always used it (see FileFilterHelper.setFilterSectionsFromFields); this is the
 * per-library twin the aggregate panel fans out across every library that binds the
 * column.
 *
 * Deliberately NOT built on getSharepointFilterValues: that issues a `<Contains>` query
 * and pages to exhaustion, so an empty search term would walk the entire library — per
 * column, per library. It stays the right tool for an explicit search, and the wrong one
 * for populating a list.
 */

/**
 * Cached for the session, keyed by list + field. Distinct-value sets change slowly, and
 * without this every reopen of a section refires the whole fan-out.
 *
 * Only SUCCESSES are cached: a transient failure returns [] and must not pin an empty
 * list for the rest of the session (the same trap fetchAggregatedListSettings documents).
 */
const filterValuesCache = new Map<string, string[]>();

const cacheKey = (listId: string, fieldInternalName: string): string =>
    `${listId}|${fieldInternalName}`;

export const clearAggregatedFilterValuesCache = (): void => {
    filterValuesCache.clear();
};

/**
 * Distinct values of ONE field in ONE library.
 *
 * Returns `[]` rather than throwing on any failure — a library the user cannot read, or
 * a field the endpoint rejects, must degrade to "contributes no suggestions" and never
 * sink the other libraries in the fan-out.
 *
 * `ViewId` is deliberately omitted, unlike the single-library caller: this grid is not
 * rendering a SharePoint view, so values should come from the whole list. `RootFolder`
 * is likewise left off — the aggregate query is `Scope="RecursiveAll"` from the library
 * root, so the unscoped default already matches it.
 */
export const fetchLibraryFilterValues = async (
    accessToken: string,
    webUrl: string,
    listId: string,
    fieldInternalName: string
): Promise<string[]> => {
    if (!webUrl || !listId || !fieldInternalName) {
        return [];
    }

    const key = cacheKey(listId, fieldInternalName);
    const cached = filterValuesCache.get(key);
    if (cached) {
        return cached;
    }

    try {
        const url = `${webUrl}/_api/web/lists('${listId}')/RenderListFilterData`
            + `?FieldInternalName=${encodeURIComponent(fieldInternalName)}`
            + `&ExcludeFieldFilteringHtml=true`;

        const response = await axiosRetryPost(url, {}, {
            headers: { Authorization: `Bearer ${accessToken}` }
        }, false);

        const values = parseFilterValueMarkup(response?.data)
            .map(item => item.value ?? item.title)
            // "(All)" is the endpoint's own select-all row, not a value. The
            // single-library builder strips it downstream by title; done here instead
            // so it can never reach the merge.
            .filter(value => typeof value === "string" && value.length > 0 && value !== "(All)");

        filterValuesCache.set(key, values);
        return values;
    } catch (error) {
        logger(error, "aggregatedFilterValuesService.ts (fetchLibraryFilterValues)");
        return [];
    }
};
